/* Таблица со сортировкой — одна на все списки (ui.js dataTable).
   Сортировка живёт по идентификатору таблицы и переживает перерисовку и переходы. */
import { useRef, useState, type ReactNode } from 'react';
import { MSort, useNilCells } from '../shell/Mobile';

export interface Col<R> {
  k: string;
  n: string;
  w?: string;
  r?: boolean;
  num?: boolean;
  sort?: (a: R, b: R) => number;
  cell: (r: R) => ReactNode;
}
const TSORT: Record<string, { k: string | null; dir: number }> = {};

export function DataTable<R extends { id: string }>({
  id,
  cols,
  rows,
  sel,
  onRow,
  onRowDouble,
  rowClass,
  fixed,
}: {
  id: string;
  cols: Col<R>[];
  rows: R[];
  sel?: string | null;
  onRow?: (r: R, e: React.MouseEvent) => void;
  onRowDouble?: (r: R) => void;
  rowClass?: (r: R) => string;
  /** Колонки держат объявленную ширину (все, кроме одной — гибкой, без `w`), длинный текст уходит в ellipsis.
      Не включать, если таблица не фиксирует ширины у всех столбцов — иначе они разделят место поровну. */
  fixed?: boolean;
}) {
  const [st, setSt] = useState(() => (TSORT[id] ||= { k: null, dir: 1 }));
  const col = cols.find((c) => c.k === st.k);
  const list = col?.sort ? [...rows].sort((a, b) => col.sort!(a, b) * st.dir) : rows;
  const sortBy = (k: string) => {
    const n = st.k === k ? { k, dir: -st.dir } : { k, dir: 1 };
    TSORT[id] = n;
    setSt(n);
  };
  const box = useRef<HTMLDivElement>(null);
  useNilCells(box);
  return (
    <div style={{ display: 'contents' }} ref={box}>
    <MSort cols={cols.filter((c) => c.sort)} cur={st.k} dir={st.dir} onSort={sortBy} />
    <div className="tblwrap">
      <table className={'tbl' + (fixed ? ' fx' : '')} data-tbl={id}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.k}
                className={(c.r ? 'r ' : '') + (c.sort ? 'srt ' : '') + (st.k === c.k ? (st.dir > 0 ? 'up' : 'dn') : '')}
                style={c.w ? { width: c.w } : undefined}
                onClick={c.sort ? () => sortBy(c.k) : undefined}
              >
                {c.n}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr
              key={r.id}
              className={'click' + (sel === r.id ? ' on' : '') + (rowClass ? ' ' + rowClass(r) : '')}
              data-row={r.id}
              onClick={onRow ? (e) => onRow(r, e) : undefined}
              onDoubleClick={onRowDouble ? () => onRowDouble(r) : undefined}
            >
              {cols.map((c) => (
                <td key={c.k} className={(c.r ? 'r ' : '') + (c.num ? 'num' : '')}>
                  {c.cell(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

export const byStr =
  <R,>(f: (r: R) => string | null | undefined) =>
  (a: R, b: R) =>
    String(f(a) || '').localeCompare(String(f(b) || ''), 'ru');
export const byNum =
  <R,>(f: (r: R) => number | null | undefined) =>
  (a: R, b: R) =>
    (f(a) || 0) - (f(b) || 0);
